/**
 * Celle (ADR-0074 Phase 3) — the deployment-shard registry the SaaS fleet runs on.
 * List / create / edit the cells, pick the DEFAULT one (the target for a new tenant
 * with no explicit choice), delete an EMPTY one. A tenant's cell is decided AT
 * CREATION (the Nuovo-tenant dialog) — moving later is a data migration, so there is
 * deliberately no "sposta tenant" here.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, Pencil, Plus, Server, Star, Trash2, TriangleAlert } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { type PlatformCell, PlatformApiError, platformApi } from "@/lib/platform";

const CELLS_KEY = ["platform", "cells"];

const STATUS_LABEL: Record<PlatformCell["status"], string> = {
  active: "Attiva",
  draining: "In svuotamento",
  disabled: "Disabilitata",
};

function StatusBadge({ status }: { status: PlatformCell["status"] }) {
  const tone = status === "active" ? "active" : status === "draining" ? "warning" : "neutral";
  return <Badge tone={tone as "active" | "warning" | "neutral"}>{STATUS_LABEL[status]}</Badge>;
}

interface CellFormState {
  name: string;
  endpoint: string;
  region: string;
  status: PlatformCell["status"];
  is_default: boolean;
}

const EMPTY_FORM: CellFormState = {
  name: "",
  endpoint: "",
  region: "",
  status: "active",
  is_default: false,
};

function CellDialog({
  cell,
  onClose,
  onSaved,
}: {
  cell: PlatformCell | null; // null = create
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = React.useState<CellFormState>(
    cell
      ? {
          name: cell.name,
          endpoint: cell.endpoint,
          region: cell.region ?? "",
          status: cell.status,
          is_default: cell.is_default,
        }
      : EMPTY_FORM,
  );
  const save = useMutation({
    mutationFn: async () => {
      if (cell) {
        return platformApi.updateCell(cell.id, {
          name: form.name,
          endpoint: form.endpoint,
          region: form.region.trim() ? form.region.trim() : null,
          status: form.status,
        });
      }
      return platformApi.createCell({
        name: form.name,
        endpoint: form.endpoint,
        region: form.region.trim() ? form.region.trim() : null,
        status: form.status,
        is_default: form.is_default,
      });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const set = (patch: Partial<CellFormState>) => setForm((f) => ({ ...f, ...patch }));
  const canSave = form.name.trim().length > 0 && form.endpoint.trim().length > 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{cell ? `Modifica cella — ${cell.name}` : "Nuova cella"}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cell-name">Nome</Label>
            <Input
              id="cell-name"
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="eu-south-1"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cell-endpoint">Endpoint interno</Label>
            <Input
              id="cell-endpoint"
              value={form.endpoint}
              onChange={(e) => set({ endpoint: e.target.value })}
              placeholder="http://cell-eu-south-1:8000"
            />
            <p className="text-xs text-muted-foreground">
              L'URL base a cui il Routing inoltra le richieste dei tenant della cella.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cell-region">Region (opzionale)</Label>
              <Input
                id="cell-region"
                value={form.region}
                onChange={(e) => set({ region: e.target.value })}
                placeholder="eu-south"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cell-status">Stato</Label>
              <select
                id="cell-status"
                value={form.status}
                onChange={(e) => set({ status: e.target.value as PlatformCell["status"] })}
              >
                <option value="active">Attiva</option>
                <option value="draining">In svuotamento</option>
                <option value="disabled">Disabilitata</option>
              </select>
            </div>
          </div>
          {!cell && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_default}
                onChange={(e) => set({ is_default: e.target.checked })}
              />
              Cella di default per i nuovi tenant
            </label>
          )}
          {save.isError && (
            <p className="text-sm text-danger">
              {save.error instanceof PlatformApiError
                ? save.error.message
                : "Salvataggio non riuscito."}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
            {cell ? "Salva" : "Crea cella"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PlatformCellsPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<PlatformCell | null>(null);

  const listQ = useQuery({ queryKey: CELLS_KEY, queryFn: () => platformApi.listCells() });
  const invalidate = () => qc.invalidateQueries({ queryKey: CELLS_KEY });

  const setDefault = useMutation({
    mutationFn: (id: string) => platformApi.setDefaultCell(id),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => platformApi.deleteCell(id),
    onSuccess: invalidate,
  });
  // The minted plaintext is shown ONCE (only its hash is stored server-side).
  const [mintedToken, setMintedToken] = React.useState<{ cell: string; token: string } | null>(
    null,
  );
  const mintToken = useMutation({
    mutationFn: (cell: PlatformCell) =>
      platformApi.mintCellBackboneToken(cell.id).then((r) => ({ cell: cell.name, token: r.token })),
    onSuccess: setMintedToken,
  });

  const rows = listQ.data ?? [];

  return (
    <PageContainer>
      <PageHeader
        title="Celle"
        description="Gli shard di deployment della flotta SaaS. La cella di un tenant si sceglie alla creazione; spostarla dopo è una migrazione."
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            Nuova cella
          </Button>
        }
      />
      <PageContent className="space-y-4">
        {remove.isError && (
          <p className="text-sm text-danger">
            {remove.error instanceof PlatformApiError &&
            remove.error.message.includes("not_empty")
              ? "La cella ospita ancora dei tenant: non può essere eliminata."
              : "Operazione non riuscita."}
          </p>
        )}
        {listQ.isError ? (
          <EmptyState
            icon={TriangleAlert}
            title="Impossibile caricare le celle"
            action={
              <Button variant="ghost" onClick={() => listQ.refetch()}>
                Riprova
              </Button>
            }
          />
        ) : rows.length === 0 && !listQ.isLoading ? (
          <EmptyState
            icon={Server}
            title="Nessuna cella registrata"
            description="Senza celle i tenant restano non assegnati (deploy co-located). Registra la prima cella per attivare il piano di routing."
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Nome</Th>
                  <Th>Endpoint</Th>
                  <Th>Region</Th>
                  <Th>Stato</Th>
                  <Th>Tenant</Th>
                  <Th className="w-44 text-right">Azioni</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((cell) => (
                  <Tr key={cell.id}>
                    <Td>
                      <span className="flex items-center gap-2 font-medium">
                        {cell.name}
                        {cell.is_default && (
                          <Badge tone="draft">
                            <Star className="mr-1 h-3 w-3" /> Default
                          </Badge>
                        )}
                      </span>
                    </Td>
                    <Td className="font-mono text-xs text-muted-foreground">{cell.endpoint}</Td>
                    <Td>{cell.region ?? "—"}</Td>
                    <Td>
                      <StatusBadge status={cell.status} />
                    </Td>
                    <Td>{cell.tenant_count}</Td>
                    <Td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!cell.is_default && (
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Rendi default"
                            onClick={() => setDefault.mutate(cell.id)}
                            disabled={setDefault.isPending}
                          >
                            <Star className="h-4 w-4" strokeWidth={1.5} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Genera token backbone"
                          disabled={mintToken.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Generare (o RUOTARE) il token backbone di "${cell.name}"? Il token precedente smette di funzionare.`,
                              )
                            )
                              mintToken.mutate(cell);
                          }}
                        >
                          <KeyRound className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Modifica"
                          onClick={() => setEditing(cell)}
                        >
                          <Pencil className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Elimina (solo se vuota)"
                          className="text-danger"
                          disabled={cell.tenant_count > 0 || remove.isPending}
                          onClick={() => {
                            if (window.confirm(`Eliminare la cella "${cell.name}"?`))
                              remove.mutate(cell.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </PageContent>
      {creating && <CellDialog cell={null} onClose={() => setCreating(false)} onSaved={invalidate} />}
      {editing && (
        <CellDialog cell={editing} onClose={() => setEditing(null)} onSaved={invalidate} />
      )}
      {mintedToken && (
        <BackboneTokenDialog minted={mintedToken} onClose={() => setMintedToken(null)} />
      )}
    </PageContainer>
  );
}

function BackboneTokenDialog({
  minted,
  onClose,
}: {
  minted: { cell: string; token: string };
  onClose: () => void;
}) {
  const [copied, setCopied] = React.useState(false);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Token backbone — {minted.cell}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-warning">
            Mostrato UNA sola volta (viene conservato solo l'hash). Mettilo nell'env della
            cella come <code className="font-mono">BACKBONE_FLEET_TOKEN</code>: il Control lo
            riconoscerà e servirà alla cella solo i SUOI eventi.
          </p>
          <div className="flex items-center gap-2 rounded-md border border-border/60 bg-surface p-3">
            <code className="flex-1 break-all font-mono text-xs">{minted.token}</code>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void navigator.clipboard?.writeText(minted.token);
                setCopied(true);
              }}
            >
              <Copy className="h-4 w-4" strokeWidth={1.5} />
              {copied ? "Copiato" : "Copia"}
            </Button>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
